import type { Component, Value } from '../../schema';
import type { SelectionsMap } from '../../state/selections';

export interface CombinedImageLayer {
    characteristicId: number;
    /** Drives both `z-index` and the stable React key. */
    sortKey: number;
    value: Value & { image: string };
}

/**
 * Resolve the ordered layer stack for the current selections. Mirrors the
 * legacy storefront's sort by `sort_order_combiimage`: lower values render
 * below, higher values on top.
 *
 * Shared between {@link CombinedImage} (DOM rendering) and the canvas
 * compositor used by ActionButton — both must agree on which layers are
 * "the current product image" so the PNG attached to the savebutton
 * payload matches what the customer actually saw.
 */
export function collectCombinedImageLayers(
    component: Component,
    selections: SelectionsMap,
): CombinedImageLayer[] {
    const layers: CombinedImageLayer[] = [];

    for (const group of component.groups) {
        for (const characteristic of group.characteristics) {
            const sel = selections[characteristic.id];
            if (!sel) continue;

            const candidateIds: number[] = [];
            if (sel.kind === 'single' && sel.valueId !== null) {
                candidateIds.push(sel.valueId);
            } else if (sel.kind === 'multi') {
                candidateIds.push(...sel.valueIds);
            } else if (sel.kind === 'quantities') {
                for (const [idStr, qty] of Object.entries(sel.quantities)) {
                    if (qty > 0) candidateIds.push(Number(idStr));
                }
            }

            if (candidateIds.length === 0) continue;
            if (!('values' in characteristic)) continue;

            for (const valueId of candidateIds) {
                const value = characteristic.values.find((v) => v.id === valueId);
                if (!value || !value.image) continue;
                layers.push({
                    characteristicId: characteristic.id,
                    sortKey: characteristic.sortOrderCombiImage,
                    value: value as Value & { image: string },
                });
            }
        }
    }

    layers.sort((a, b) => a.sortKey - b.sortKey);
    return layers;
}
